import { useEffect, useRef, useState, KeyboardEvent, ChangeEvent } from "react";
import { Box } from "@mui/material";
import strings from "global/constants/StringConstants";
import { PromptRect } from "../AiPreviewPromptHelper";
import styles from "../AiWebForm.styles";
import { AiPromptGlyph, ArrowUpGlyph } from "./AiWebFormIcons";

const copy = strings.aiWebForm;

interface Props {
  anchorRect: PromptRect | null;
  iframeRect?: PromptRect | null;
  selectedText: string;
  onSubmit: (instruction: string) => void;
  disabled?: boolean;
  onClose: () => void;
}

const PILL_WIDTH = 340;
const PILL_HEIGHT = 40;
const GAP = 8;

export const AiSelectionPrompt = ({
  anchorRect,
  iframeRect,
  selectedText,
  onSubmit,
  disabled = false,
  onClose,
}: Props) => {
  const [instruction, setInstruction] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 600);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        if (inputRef.current) {
          inputRef.current.focus({ preventScroll: true });
        }
      } catch {}
    }, 20);
    return () => clearTimeout(timer);
  }, [anchorRect]);

  const canSend = !disabled && instruction.trim().length > 0;

  const handleSend = () => {
    if (!canSend) return;
    onSubmit(instruction.trim());
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if ((e.ctrlKey || e.metaKey) && (e.key === "c" || e.key === "C")) {
      const el = inputRef.current;
      const hasInputSelection = el && el.selectionStart !== el.selectionEnd;
      if (!hasInputSelection && selectedText) {
        navigator.clipboard?.writeText(selectedText);
      }
    }
  };

  if (!anchorRect) return null;

  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  // Horizontal position: center over selection
  const centerX = anchorRect.left + anchorRect.width / 2;
  let left = centerX - PILL_WIDTH / 2;

  // Clamp within iframe boundaries so it stays over the preview area
  const minLeft = iframeRect ? Math.max(12, iframeRect.left + 8) : 12;
  const maxLeft = iframeRect
    ? Math.min(vw - PILL_WIDTH - 12, iframeRect.right - PILL_WIDTH - 8)
    : vw - PILL_WIDTH - 12;

  if (maxLeft >= minLeft) {
    left = Math.max(minLeft, Math.min(left, maxLeft));
  } else {
    left = Math.max(12, Math.min(left, vw - PILL_WIDTH - 12));
  }

  // Vertical position: prefer above the selection
  let top = anchorRect.top - PILL_HEIGHT - GAP;

  // If not enough room above, place below
  const minTop = iframeRect ? Math.max(12, iframeRect.top + 4) : 12;
  if (top < minTop) {
    top = anchorRect.bottom + GAP;
  }

  // Clamp to viewport
  if (top + PILL_HEIGHT > vh - 12) {
    top = Math.max(12, vh - PILL_HEIGHT - 12);
  }

  return (
    <Box
      ref={containerRef}
      role="dialog"
      aria-label={copy.promptAriaLabel}
      onMouseDown={(e) => e.stopPropagation()}
      sx={styles.selectionPrompt({ top, left, isMobile })}
    >
      <AiPromptGlyph sx={styles.selectionPromptIcon} />
      <Box
        component="input"
        type="text"
        ref={inputRef}
        value={instruction}
        placeholder={copy.promptLabel}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setInstruction(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={copy.promptLabel}
        sx={styles.selectionPromptInput}
      />
      <Box
        component="button"
        type="button"
        onClick={handleSend}
        disabled={disabled || !canSend}
        aria-label={copy.send}
        sx={styles.selectionPromptSend(canSend)}
      >
        <ArrowUpGlyph />
      </Box>
    </Box>
  );
};
